/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import { UnifiedCollectionType } from 'client-code/previews';

import { TIVO_SERVICES } from '../../../config';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { TUnifiedCollectionPreview } from '../../../core/types';
import { PreviewComponent, PreviewComponentAssets } from '../../../core/usecases';
import { DateTime } from '../../../core/utils';
import {
    ActionButton,
    Badge,
    ContentDescription,
    ContentTitle,
    Credits,
    Divider,
    Genres,
    ParentalControlRating,
    Separator,
    ShowcaseBanner,
} from '../ui-kit';
import { InfoCardProps } from './InfoCardProps';

const SeriesInfoCard: FunctionComponent<InfoCardProps> = ({
    metadata,
    isActionMode,
    userActions,
    highlight,
    properties,
}) => {
    const oPreviewAssets = new PreviewComponentAssets();
    const {
        unifiedCollectionType,
        title = '',
        description = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.DESCRIPTION_NOT_AVAILABLE,
        ),
        unifiedCollectionId = '',
        internalRatingList = [],
        channel,
        creditString = '',
        categoryLabel = '',
        premierYear,
        startTime,
        endTime,
    } = metadata as TUnifiedCollectionPreview;

    if (unifiedCollectionType !== UnifiedCollectionType.SeriesPreviewV2) return null;

    const assetService = new ImageAssetsService({
        imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
    });

    const pcRatingNames = PreviewComponent.getParentalRatingNames(internalRatingList);
    const url: string = assetService.getShowcaseBannerImageUrlForVodContent(unifiedCollectionId);
    const firstAiredYear = premierYear || new DateTime(startTime).strftime('%Y');
    const lastAiredYear = new DateTime(endTime).strftime('%Y');
    const isFirstLastAiredYearSame = firstAiredYear === lastAiredYear;

    const drawInfoCard = () => {
        return (
            <div className="info-card-container">
                {/* <div className="info-card metadata-only"> 
                to show metadata only in scroll mode */}
                <div className="info-card">
                    <div className="info-card-header">
                        <div
                            className={
                                properties?.hasItemsBefore
                                    ? 'left-nav-arrow'
                                    : 'left-nav-arrow hidden'
                            }
                        ></div>
                        {/* <div className="showcase-banner-metadata">
                        to switch between banner with metadata view */}
                        <div className="showcase-banner-metadata banner-only">
                            <ShowcaseBanner bannerUrl={url} fallbackText={title} />
                        </div>
                        <div
                            className={
                                properties?.hasItemsAfter
                                    ? 'right-nav-arrow'
                                    : 'right-nav-arrow hidden'
                            }
                        ></div>
                    </div>
                    <div className="info-card-body">
                        <ContentTitle title={title} />

                        <div className="uikit line-height-1">
                            <Badge text={channel?.videoResolution} uppercase={true} />
                            <Badge text={'New'} />
                            <ParentalControlRating ratingNames={pcRatingNames} />
                            <Separator />
                            <Genres categoryLabel={categoryLabel} />
                            <Separator />
                            <span className="uikit primary-text-style muted-color text-light">
                                {isFirstLastAiredYearSame
                                    ? `${firstAiredYear}`
                                    : `${firstAiredYear} - ${lastAiredYear}`}
                            </span>
                        </div>

                        <Credits
                            creditString={creditString}
                            lines={1}
                            className="uikit primary-text-style muted-color text-light line-height-mt-zero"
                        />
                        <ContentDescription description={description} />
                        <Credits creditString={creditString} />
                    </div>
                    <div className="info-card-footer">
                        <Divider />
                        {!isActionMode && <ActionButton type="options" highlighted={true} />}
                        {isActionMode &&
                            (userActions.length > 0 ? (
                                userActions.map((userAction, idx) => (
                                    <ActionButton
                                        key={userAction.id}
                                        highlighted={idx === highlight}
                                    >
                                        {userAction.text}
                                    </ActionButton>
                                ))
                            ) : (
                                <ActionButton type="info" highlighted={true} />
                            ))}
                    </div>
                </div>
            </div>
        );
    };
    return <React.Fragment>{drawInfoCard()}</React.Fragment>;
};

export default SeriesInfoCard;
